import { useEffect, useRef, useMemo } from "react";
import {
  SubscriptionProduct,
  useGetCurrentStripeAccountLazyQuery,
  useGetPreviewInvoiceLazyQuery,
} from "gql/generated.graphql";
import {
  SubscriptionChangeMode,
  PricingDetails,
  PaymentMethod,
  Invoice,
  InvoiceItem,
} from "./SubscriptionChangeConfirmationModalV2.interfaces";

interface UseSubscriptionChangeDataProps {
  isOpen: boolean;
  newProduct: SubscriptionProduct | null;
  mode: SubscriptionChangeMode;
  promoCode: string | undefined;
}

export const useSubscriptionChangeData = ({
  isOpen,
  newProduct,
  mode,
  promoCode,
}: UseSubscriptionChangeDataProps) => {
  const hasFetchedRef = useRef(false);

  const [getStripeAccountLazyQuery, { data: stripeAccountData }] =
    useGetCurrentStripeAccountLazyQuery({
      fetchPolicy: "network-only",
    });

  const [
    getPreviewInvoiceLazyQuery,
    { data: invoiceData, loading: loadingInvoice, error },
  ] = useGetPreviewInvoiceLazyQuery({
    fetchPolicy: "network-only",
  });

  // Fetch data only once when modal opens with valid data
  useEffect(() => {
    if (isOpen && newProduct && !hasFetchedRef.current) {
      hasFetchedRef.current = true;

      getStripeAccountLazyQuery();
      getPreviewInvoiceLazyQuery({
        variables: {
          input: {
            mode: mode,
            newStripePriceId: newProduct.stripePriceId || "",
            promoCode: undefined,
          },
        },
      });
    }
  }, [
    isOpen,
    newProduct,
    mode,
    getStripeAccountLazyQuery,
    getPreviewInvoiceLazyQuery,
  ]);

  // Refetch when promo code changes
  useEffect(() => {
    if (promoCode !== undefined && hasFetchedRef.current && newProduct) {
      getPreviewInvoiceLazyQuery({
        variables: {
          input: {
            mode: mode,
            newStripePriceId: newProduct.stripePriceId || "",
            promoCode: promoCode,
          },
        },
      });
    }
  }, [promoCode, mode, newProduct, getPreviewInvoiceLazyQuery]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasFetchedRef.current = false;
    }
  }, [isOpen]);

  const stripeAccount = stripeAccountData?.getCurrentStripeAccount;
  const defaultPaymentMethod: PaymentMethod | undefined =
    stripeAccount?.paymentMethods?.at(0);
  const invoice = invoiceData?.getPreviewInvoice;

  return {
    invoice,
    loadingInvoice,
    error,
    defaultPaymentMethod,
  };
};

interface UseSubscriptionPricingProps {
  newProduct: SubscriptionProduct | null;
  invoice: Invoice | undefined;
}

export const useSubscriptionPricing = ({
  newProduct,
  invoice,
}: UseSubscriptionPricingProps): PricingDetails => {
  return useMemo(() => {
    if (!newProduct) {
      return { planPrice: 0, creditAmount: 0, discountAmount: 0, totalDue: 0 };
    }

    // Invoice amounts and newProduct.price are both in dollars
    const base = newProduct.price || 0;
    const credit =
      invoice?.items?.find((item: InvoiceItem) => item.amount < 0)?.amount || 0;
    const total = invoice?.amountDue ?? base;
    const plan =
      invoice?.items?.find((item: InvoiceItem) => item.amount > 0)?.amount ||
      base;

    // Calculate discount from coupon
    let discount = 0;
    if (invoice?.coupon) {
      if (invoice.coupon.amountOff) {
        // amountOff is in cents from Stripe, convert to dollars
        discount = invoice.coupon.amountOff / 100;
      } else if (invoice.coupon.percentOff) {
        // Calculate percentage discount from the plan price
        discount = (plan * invoice.coupon.percentOff) / 100;
      }
    }

    return {
      planPrice: plan,
      creditAmount: credit,
      discountAmount: discount,
      totalDue: total,
      coupon: invoice?.coupon,
    };
  }, [newProduct, invoice]);
};
